import { MOTION, RADIUS } from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { type ChangeEvent, useRef, useState } from "react";

/**
 * The one-time code, drawn as one box per digit.
 *
 * Behind the boxes there is still a single `<input>`, not one per digit: a code is
 * pasted, autofilled from the mail app and corrected with backspace at least as often
 * as it is typed, and each of those is browser behaviour we would have to re-implement
 * across six fields. The input is transparent and covers the whole row; the boxes below
 * render what it holds. Autofill, paste and the numeric keypad keep working untouched,
 * and the field is still one control to a screen reader — and to a spec that fills it.
 */

interface CodeFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** How many digits the code has. The API tells us; it is not ours to assume. */
  length: number;
  /** The accessible name: the boxes replace the label a filled field would show. */
  label: string;
  error?: boolean;
  autoFocus?: boolean;
  testId?: string;
}

/** px — matches the height of a filled `TextField`, so a form of both lines up. */
const BOX_HEIGHT = 56;

/** px — the caret, sized against the digit rather than the box. */
const CARET_WIDTH = 2;
const CARET_HEIGHT = 26;

export function CodeField({
  value,
  onChange,
  length,
  label,
  error = false,
  autoFocus = false,
  testId,
}: CodeFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  /** Digits only, and never more than fit — so a pasted "123 456" still lands. */
  const handleChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange(event.target.value.replace(/\D/g, "").slice(0, length));

  /**
   * The boxes fill strictly left to right, so a caret dropped into the middle of the
   * value by a tap would insert digits where the display cannot show them. Whatever
   * moved it, it goes back to the end.
   */
  const pinCaret = () => {
    const input = inputRef.current;
    if (!input) return;
    const end = input.value.length;
    if (input.selectionStart !== end || input.selectionEnd !== end)
      input.setSelectionRange(end, end);
  };

  const caretAt = focused && value.length < length ? value.length : -1;
  const activeAt = focused ? Math.min(value.length, length - 1) : -1;

  return (
    <Box sx={{ position: "relative", display: "flex", gap: 1 }}>
      {Array.from({ length }, (_, index) => (
        <Box
          key={index}
          sx={{
            position: "relative",
            // Six equal boxes across whatever the sheet is wide, phone or desktop.
            flex: 1,
            minWidth: 0,
            height: `${BOX_HEIGHT}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: `${RADIUS.md}px`,
            // The same fill and hairline a filled field uses — a field is an outline
            // in both schemes, never a slab that changes weight with what is behind it.
            backgroundColor: "background.default",
            border: "1px solid",
            borderColor: error
              ? "error.main"
              : index === activeAt
                ? "primary.main"
                : "divider",
            transition: `border-color ${MOTION.transition}`,
          }}
        >
          <Typography
            component="span"
            variant="H2"
            sx={{ color: "text.primary" }}
          >
            {value[index] ?? ""}
          </Typography>

          {index === caretAt ? (
            <Box
              sx={{
                position: "absolute",
                width: `${CARET_WIDTH}px`,
                height: `${CARET_HEIGHT}px`,
                borderRadius: `${CARET_WIDTH / 2}px`,
                backgroundColor: "primary.main",
                // Ends on the visible frame, so the reduced-motion rule that collapses
                // every animation to 0.01ms leaves a caret that is there rather than one
                // that blinked out and stayed out.
                "@keyframes elyxeer-caret-blink": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0 },
                },
                animation: "elyxeer-caret-blink 1.1s step-end infinite",
              }}
            />
          ) : null}
        </Box>
      ))}

      <Box
        component="input"
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onSelect={pinCaret}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus={autoFocus}
        aria-label={label}
        data-testid={testId}
        sx={{
          // Covers the row: a tap anywhere along it opens the keypad on the digit next
          // to be typed, because there is only ever one field to focus.
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          margin: 0,
          padding: 0,
          border: 0,
          outline: "none",
          background: "transparent",
          // The boxes draw the value; the input only has to catch the typing. Its own
          // caret goes too — the one we draw sits in the box, where the digit will land.
          color: "transparent",
          caretColor: "transparent",
          textAlign: "center",
          // 16px, or iOS zooms the viewport the moment the field takes focus.
          fontSize: "16px",
          fontFamily: "inherit",
        }}
      />
    </Box>
  );
}
